/**
 * Layout Primitives
 * Building blocks for page structure. Simple, composable, intentional.
 */

import { ReactNode } from 'react';

/* ========== Container ========== */

interface ContainerProps {
  children: ReactNode;
  narrow?: boolean;
  className?: string;
}

export function Container({ children, narrow, className = '' }: ContainerProps) {
  const baseClass = narrow ? 'page-container-narrow' : 'page-container';
  return <div className={`${baseClass} ${className}`}>{children}</div>;
}

/* ========== Section ========== */

interface SectionProps {
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  background?: 'default' | 'subtle' | 'muted' | 'gradient' | 'gradient-teal';
}

export function Section({
  children,
  className = '',
  size = 'md',
  background = 'default',
}: SectionProps) {
  const sizeClasses = {
    sm: 'py-12 md:py-16',
    md: 'py-20 md:py-32',
    lg: 'py-24 md:py-40',
  };

  const bgClasses = {
    default: 'bg-surface',
    subtle: 'bg-surface-subtle',
    muted: 'bg-surface-muted',
    gradient: 'gradient-hero',
    'gradient-teal': 'gradient-teal-subtle',
  };

  return (
    <section className={`${sizeClasses[size]} ${bgClasses[background]} ${className}`}>
      {children}
    </section>
  );
}

/* ========== Stack ========== */

interface StackProps {
  children: ReactNode;
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export function Stack({ children, gap = 'md', className = '' }: StackProps) {
  const gapClasses = {
    xs: 'space-y-2',
    sm: 'space-y-4',
    md: 'space-y-6',
    lg: 'space-y-8',
    xl: 'space-y-12',
  };

  return <div className={`${gapClasses[gap]} ${className}`}>{children}</div>;
}

/* ========== Grid ========== */

interface GridProps {
  children: ReactNode;
  cols?: 1 | 2 | 3;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function Grid({ children, cols = 2, gap = 'md', className = '' }: GridProps) {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  };

  const gapClasses = {
    sm: 'gap-4 md:gap-6',
    md: 'gap-6 md:gap-8',
    lg: 'gap-8 md:gap-12',
  };

  return (
    <div className={`grid ${colClasses[cols]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
}

/* ========== Spacer ========== */

interface SpacerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Spacer({ size = 'md' }: SpacerProps) {
  const sizeClasses = {
    sm: 'h-8 md:h-12',
    md: 'h-12 md:h-20',
    lg: 'h-20 md:h-32',
    xl: 'h-32 md:h-40',
  };

  return <div className={sizeClasses[size]} />;
}

/* ========== Divider ========== */

interface DividerProps {
  className?: string;
}

export function Divider({ className = '' }: DividerProps) {
  return <hr className={`divider ${className}`} />;
}
